import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    formatDateAndTime,
    formatRelativeTime,
} from '@apps/web/lib/date_utils';
import {
    type EventRead,
    useEntityEvents,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const MAX_EVENTS = 100;

type EntityEventsTimelineProps = {
    readonly organizationId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly includeRelated?: boolean;
    readonly emptyMessage?: string;
    // 'admin' routes through /admin/.../activity (requires User.admin);
    // 'org' (default) routes through /organizations/.../activity (requires
    // OrgMemberProfile).
    readonly scope?: 'org' | 'admin';
};

function describeActor(event: EventRead): string {
    if (event.actor_user_name) {
        return event.actor_user_name;
    }
    switch (event.actor_type) {
        case 'user':
            return 'A user';
        case 'phoebe_admin':
            return 'Phoebe Support';
        case 'system':
            return 'Phoebe';
        default:
            return 'Unknown';
    }
}

type EventDelta = {
    readonly before: Record<string, unknown>;
    readonly after: Record<string, unknown>;
};

function extractDelta(event: EventRead): EventDelta | null {
    const raw = (event.data as { readonly delta?: unknown })?.delta;
    if (raw === null || typeof raw !== 'object') {
        return null;
    }
    const delta = raw as {
        readonly before?: unknown;
        readonly after?: unknown;
    };
    if (
        delta.before === null ||
        typeof delta.before !== 'object' ||
        delta.after === null ||
        typeof delta.after !== 'object'
    ) {
        return null;
    }
    return {
        before: delta.before as Record<string, unknown>,
        after: delta.after as Record<string, unknown>,
    };
}

const AVAILABILITY_TRIGGER_LABELS: Readonly<Record<string, string>> = {
    coordinator_edit: 'coordinator edit',
    coordinator_chat_parser: 'coordinator chat',
    caregiver_self_report: 'caregiver self-report',
    sms_agent: 'SMS',
    survey: 'survey',
    sync: 'sync',
};

const OUTREACH_STATUS_SOURCE_LABELS: Readonly<Record<string, string>> = {
    message_outreach_tick: 'scheduled outreach',
    survey_outreach_tick: 'scheduled survey',
    caregiver_preference_survey_tick: 'caregiver preference survey',
    temporal_callout_finalize: 'callout finalization',
    sibling_shift_assignment_reconciliation: 'shift assignment reconciliation',
    shift_discovery_reconciliation_target_create_failed:
        'shift discovery reconciliation',
    shift_discovery_reconciled_into_outreach: 'shift discovery reconciliation',
    outreach_manager_stale_paused: 'outreach manager: paused outreach expired',
    outreach_manager_expired_pending:
        'outreach manager: pending outreach expired',
    outreach_manager_all_shifts_cancelled:
        'outreach manager: all shifts were cancelled',
    message_sms_dispatch: 'SMS dispatch',
    callout_response_auto_assign: 'automatic callout assignment',
    callout_fill_shifts: 'automatic shift filling',
    agent_shift_management_cancel: 'agent shift management',
    agent_outreach_action: 'agent outreach action',
};

const EHR_SOURCE_LABELS: Readonly<Record<string, string>> = {
    wellsky: 'WellSky',
    axiscare: 'AxisCare',
    hhaexchange: 'HHAeXchange',
};

// Legacy availability summaries persisted the trigger inline as
// "Availability … (sms_agent)". Don't append a "from X" suffix for
// those or the timeline renders the label twice.
const LEGACY_TRIGGER_SUFFIX_RE = /\([a-z_][a-z0-9_ -]*\)\s*$/i;

function readTriggerLabel(event: EventRead): string | null {
    const raw = (event.data as { readonly trigger?: unknown })?.trigger;
    if (typeof raw !== 'string' || raw === '') {
        return null;
    }
    if (LEGACY_TRIGGER_SUFFIX_RE.test(event.summary)) {
        return null;
    }
    return AVAILABILITY_TRIGGER_LABELS[raw] ?? raw;
}

function readOutreachStatusSourceLabel(event: EventRead): string | null {
    if (event.event_name !== 'outreach.status_changed') {
        return null;
    }
    const raw = (event.data as { readonly source?: unknown })?.source;
    if (typeof raw !== 'string' || raw === '') {
        return null;
    }
    if (raw.endsWith('_shift_replacement')) {
        const sourceSystem = raw.slice(0, -'_shift_replacement'.length);
        const ehr = EHR_SOURCE_LABELS[sourceSystem];
        return ehr
            ? `EHR sync (${ehr}): a shift was replaced and needs review`
            : 'EHR sync: a shift was replaced and needs review';
    }
    if (raw.startsWith('clock_reminder_tick_')) {
        return 'clock reminder';
    }
    return OUTREACH_STATUS_SOURCE_LABELS[raw] ?? null;
}

function findSurveyOutreachId(event: EventRead): string | null {
    // Availability events emitted from a survey reply include the survey
    // outreach in `related_entities`. We surface that as a link on the
    // trigger label so operators can jump to the originating outreach.
    const trigger = (event.data as { readonly trigger?: unknown })?.trigger;
    if (trigger !== 'survey') {
        return null;
    }
    const outreach = event.related_entities.find(
        (ref) => ref['type'] === 'outreach' && typeof ref['id'] === 'string',
    );
    return outreach ? (outreach['id'] as string) : null;
}

function formatDeltaValue(value: unknown): {
    readonly text: string;
    readonly multiline: boolean;
} {
    if (value === null || value === undefined) {
        return { text: '∅', multiline: false };
    }
    if (typeof value === 'string') {
        return {
            text: value === '' ? '""' : value,
            multiline: value.includes('\n'),
        };
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return { text: String(value), multiline: false };
    }
    const pretty = JSON.stringify(value, null, 2);
    return { text: pretty ?? String(value), multiline: true };
}

function DeltaScalarInline({ value }: { readonly value: unknown }) {
    const formatted = formatDeltaValue(value);
    return <span>{formatted.text}</span>;
}

function DeltaScalarBlock({ value }: { readonly value: unknown }) {
    const formatted = formatDeltaValue(value);
    return (
        <pre className="bg-muted/50 border-border/60 mt-0.5 rounded border p-1 font-mono text-[11px] whitespace-pre-wrap">
            {formatted.text}
        </pre>
    );
}

function scalarIsMultiline(value: unknown): boolean {
    return formatDeltaValue(value).multiline;
}

type DeltaEntry = { readonly id: string; readonly name: string };

type EntriesDelta = {
    readonly added: ReadonlyArray<DeltaEntry>;
    readonly removed: ReadonlyArray<DeltaEntry>;
};

function extractEntries(side: unknown): ReadonlyArray<DeltaEntry> | null {
    if (side === null || typeof side !== 'object') {
        return null;
    }
    const maybeEntries = (side as { readonly entries?: unknown }).entries;
    if (!Array.isArray(maybeEntries)) {
        return null;
    }
    const entries: Array<DeltaEntry> = [];
    for (const entry of maybeEntries) {
        if (entry && typeof entry === 'object') {
            const id = (entry as { readonly id?: unknown }).id;
            const name = (entry as { readonly name?: unknown }).name;
            if (typeof id === 'string') {
                entries.push({
                    id,
                    name: typeof name === 'string' ? name : '',
                });
            }
        }
    }
    return entries;
}

function computeEntriesDelta(delta: EventDelta): EntriesDelta | null {
    const before = extractEntries(delta.before);
    const after = extractEntries(delta.after);
    if (before === null || after === null) {
        return null;
    }
    const beforeById = new Map(before.map((entry) => [entry.id, entry]));
    const afterById = new Map(after.map((entry) => [entry.id, entry]));
    const added = after.filter((entry) => !beforeById.has(entry.id));
    const removed = before.filter((entry) => !afterById.has(entry.id));
    return { added, removed };
}

function entryLabel(entry: DeltaEntry): string {
    return entry.name || entry.id;
}

export function EntityEventsTimeline({
    organizationId,
    entityType,
    entityId,
    includeRelated = true,
    emptyMessage = 'No activity yet.',
    scope = 'org',
}: EntityEventsTimelineProps) {
    const { timezone } = useOrganizationTimezone();
    const { events, isLoading, hasMore, error } = useEntityEvents({
        organizationId,
        entityType,
        entityId,
        includeRelated,
        pageSize: MAX_EVENTS,
        scope,
    });

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2 p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-xs">Loading activity…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-xs text-red-600">
                Failed to load activity.
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className="text-muted-foreground p-4 text-xs">
                {emptyMessage}
            </div>
        );
    }

    return (
        <div className="relative pb-6 pl-4">
            <div className="bg-border absolute top-3 bottom-9 left-[23px] w-px" />
            <div className="space-y-3">
                {events.map((event) => (
                    <EventRow
                        key={event.id}
                        event={event}
                        timezone={timezone}
                    />
                ))}
                {hasMore ? (
                    <div className="text-muted-foreground pl-6 text-[11px]">
                        Showing the most recent {MAX_EVENTS} events.
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function deltaIsCompact(delta: EventDelta): boolean {
    const entriesDelta = computeEntriesDelta(delta);
    if (entriesDelta) {
        return entriesDelta.added.length + entriesDelta.removed.length <= 1;
    }
    const keys = Object.keys(delta.before);
    if (keys.length > 1) {
        return false;
    }
    // A single-key scalar delta is only "compact" when both values fit on
    // one line — a nested JSON value still deserves its own expand toggle.
    return keys.every(
        (key) =>
            !formatDeltaValue(delta.before[key]).multiline &&
            !formatDeltaValue(delta.after[key]).multiline,
    );
}

function EventRow({
    event,
    timezone,
}: {
    readonly event: EventRead;
    readonly timezone: string;
}) {
    const delta = extractDelta(event);
    const triggerLabel = readTriggerLabel(event);
    const outreachStatusSourceLabel = readOutreachStatusSourceLabel(event);
    const surveyOutreachId = findSurveyOutreachId(event);
    const [expanded, setExpanded] = useState(false);
    const compact = delta !== null && deltaIsCompact(delta);
    const alwaysShowDelta = delta !== null && compact;
    const toggleableDelta = delta !== null && !compact;
    return (
        <div className="relative flex items-start gap-3">
            <div className="border-border bg-background ring-background relative z-10 mt-1.5 h-3.5 w-3.5 flex-none rounded-full border-2 ring-2" />
            <div className="min-w-0 flex-1 text-xs leading-relaxed">
                <div className="text-foreground">
                    {event.summary}
                    {triggerLabel ? (
                        <span className="text-muted-foreground">
                            {' from '}
                            {surveyOutreachId ? (
                                <Link
                                    to="/surveys/$survey-id"
                                    params={{
                                        'survey-id': surveyOutreachId,
                                    }}
                                    className="decoration-muted-foreground/70 underline decoration-dotted underline-offset-2 hover:decoration-solid"
                                >
                                    {triggerLabel}
                                </Link>
                            ) : (
                                triggerLabel
                            )}
                        </span>
                    ) : null}
                    {outreachStatusSourceLabel ? (
                        <span className="text-muted-foreground">
                            {' from '}
                            {outreachStatusSourceLabel}
                        </span>
                    ) : null}
                </div>
                <div className="text-muted-foreground">
                    {describeActor(event)}
                    {' · '}
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <span className="decoration-muted-foreground/70 underline decoration-dotted underline-offset-2">
                                {formatRelativeTime(event.occurred_at)}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {formatDateAndTime(event.occurred_at, timezone)}
                        </TooltipContent>
                    </Tooltip>
                </div>
                {alwaysShowDelta && delta ? (
                    <DeltaDetails delta={delta} />
                ) : null}
                {toggleableDelta && delta ? (
                    <Fragment>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpanded((prev) => !prev)}
                            className="text-muted-foreground mt-0.5 inline-flex h-5 gap-0.5 px-1 py-0 text-[11px]"
                            aria-expanded={expanded}
                        >
                            {expanded ? (
                                <ChevronDown className="h-3 w-3" />
                            ) : (
                                <ChevronRight className="h-3 w-3" />
                            )}
                            {expanded ? 'Hide details' : 'Show details'}
                        </Button>
                        {expanded ? <DeltaDetails delta={delta} /> : null}
                    </Fragment>
                ) : null}
            </div>
        </div>
    );
}

function DeltaDetails({ delta }: { readonly delta: EventDelta }) {
    const entriesDelta = computeEntriesDelta(delta);
    if (entriesDelta) {
        if (
            entriesDelta.added.length === 0 &&
            entriesDelta.removed.length === 0
        ) {
            return (
                <div className="border-border/60 text-muted-foreground mt-1 border-l pl-2 text-[11px]">
                    No list changes.
                </div>
            );
        }
        return (
            <ul className="border-border/60 mt-1 space-y-0.5 border-l pl-2 text-[11px]">
                {entriesDelta.added.map((entry) => (
                    <li
                        key={`add-${entry.id}`}
                        className="text-green-700 dark:text-green-400"
                    >
                        + {entryLabel(entry)}
                    </li>
                ))}
                {entriesDelta.removed.map((entry) => (
                    <li
                        key={`remove-${entry.id}`}
                        className="text-red-700 dark:text-red-400"
                    >
                        − {entryLabel(entry)}
                    </li>
                ))}
            </ul>
        );
    }
    return (
        <ul className="border-border/60 text-muted-foreground mt-1 space-y-1 border-l pl-2 font-mono text-[11px]">
            {Object.keys(delta.before).map((key) => {
                const before = delta.before[key];
                const after = delta.after[key];
                const multiline =
                    scalarIsMultiline(before) || scalarIsMultiline(after);
                if (multiline) {
                    return (
                        <li key={key} className="space-y-0.5">
                            <span className="text-muted-foreground">
                                {key}:
                            </span>
                            <div className="text-muted-foreground">Before</div>
                            <DeltaScalarBlock value={before} />
                            <div className="text-muted-foreground">After</div>
                            <DeltaScalarBlock value={after} />
                        </li>
                    );
                }
                return (
                    <li key={key}>
                        <span className="text-muted-foreground">{key}:</span>{' '}
                        <span className="decoration-muted-foreground/70 line-through">
                            <DeltaScalarInline value={before} />
                        </span>{' '}
                        →{' '}
                        <span className="text-foreground">
                            <DeltaScalarInline value={after} />
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}
